import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../i18n';
import { useAuth } from '../context/AuthContext';

const CODE_LENGTH = 6;
const RESEND_SECONDS = 60;

const INPUT_CLASS = 'w-full px-4 py-3 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-foreground';
const BUTTON_CLASS = 'w-full bg-primary text-primary-foreground py-3 rounded-lg font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors';
const LINK_BUTTON_CLASS = 'text-sm text-primary hover:underline disabled:no-underline disabled:opacity-50 disabled:cursor-not-allowed';

const normalizeCode = (value) => value.toUpperCase().replace(/[\s-]/g, '').slice(0, CODE_LENGTH);

// One screen for signing in and signing up: Google, or email then an emailed code.
function LoginPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { requestCode, verifyCode, isAuthenticated, startGoogleLogin } = useAuth();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState('email');
  const [newAccount, setNewAccount] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam === 'google_cancelled') {
      setError(t('auth.login.googleCancelled'));
    } else if (errorParam === 'google_failed') {
      setError(t('auth.login.googleFailed'));
    }
  }, [searchParams, t]);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const timer = setTimeout(() => setResendIn(resendIn - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  const finish = (result) => navigate(result.isNewUser ? '/language-setup' : '/dashboard');

  const sendCode = async (e) => {
    if (e) e.preventDefault();
    setError(null);
    setLoading(true);
    const result = await requestCode(email.trim());
    setLoading(false);
    if (result.success && result.user) {
      finish(result);
    } else if (result.success) {
      setNewAccount(result.newAccount);
      setStep('code');
      setCode('');
      setResendIn(RESEND_SECONDS);
    } else {
      setError(result.error || t('auth.code.failed'));
    }
  };

  const checkCode = async (value) => {
    setError(null);
    setLoading(true);
    const result = await verifyCode({ email: email.trim(), code: value });
    setLoading(false);
    if (result.success) {
      finish(result);
      return;
    }
    setError(result.error || t('auth.code.failed'));
    setCode('');
  };

  const changeCode = (value) => {
    const normalized = normalizeCode(value);
    setCode(normalized);
    if (normalized.length === CODE_LENGTH && !loading) checkCode(normalized);
  };

  const changeEmail = () => {
    setStep('email');
    setCode('');
    setError(null);
  };

  const handleGoogleLogin = async () => {
    setError(null);
    setGoogleLoading(true);
    await startGoogleLogin();
    setGoogleLoading(false);
  };

  const errorBox = error && (
    <div className="bg-destructive/20 border border-destructive text-destructive-foreground px-4 py-3 rounded">
      {error}
    </div>
  );

  if (step === 'code') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-card rounded-lg shadow-xl p-6 sm:p-8 border border-border">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">
              {newAccount ? t('auth.code.createTitle') : t('auth.code.signInTitle')}
            </h1>
            <p className="text-muted-foreground">
              {newAccount
                ? t('auth.code.createText', { email: email.trim() })
                : t('auth.code.signInText', { email: email.trim() })}
            </p>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              checkCode(code);
            }}
            className="space-y-6"
          >
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-foreground mb-2">
                {t('auth.code.label')}
              </label>
              <input
                type="text"
                id="code"
                value={code}
                onChange={(e) => changeCode(e.target.value)}
                className={`${INPUT_CLASS} text-center text-2xl tracking-[0.5em] uppercase`}
                autoComplete="one-time-code"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                maxLength={CODE_LENGTH}
                autoFocus
                required
                disabled={loading}
              />
            </div>

            {errorBox}

            <button type="submit" disabled={loading || code.length !== CODE_LENGTH} className={BUTTON_CLASS}>
              {loading ? t('auth.code.checking') : newAccount ? t('auth.code.createAccount') : t('auth.code.signIn')}
            </button>
          </form>

          <div className="mt-6 flex flex-col items-center space-y-2">
            <button type="button" onClick={() => sendCode()} disabled={loading || resendIn > 0} className={LINK_BUTTON_CLASS}>
              {resendIn > 0 ? t('auth.code.resendIn', { seconds: resendIn }) : t('auth.code.resend')}
            </button>
            <button type="button" onClick={changeEmail} className={LINK_BUTTON_CLASS}>
              {t('auth.code.differentEmail')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-card rounded-lg shadow-xl p-6 sm:p-8 border border-border">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            {t('auth.login.title')}
          </h1>
          <p className="text-muted-foreground">
            {t('auth.login.subtitle')}
          </p>
        </div>

        <button
          type="button"
          data-track="login-google"
          onClick={handleGoogleLogin}
          disabled={loading || googleLoading}
          className="w-full flex items-center justify-center gap-3 bg-card border border-border text-foreground py-3 rounded-lg font-semibold hover:bg-secondary/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors mb-6"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {googleLoading ? t('auth.login.redirecting') : t('auth.login.continueWithGoogle')}
        </button>

        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-card text-muted-foreground">{t('auth.login.orSignInWithEmail')}</span>
          </div>
        </div>

        <form onSubmit={sendCode} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
              {t('auth.login.email')}
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={INPUT_CLASS}
              placeholder={t('auth.login.emailPlaceholder')}
              autoComplete="email"
              required
              disabled={loading}
            />
            <p className="text-sm text-muted-foreground mt-2">{t('auth.login.emailHint')}</p>
          </div>

          {errorBox}

          <button type="submit" data-track="login-send-code" disabled={loading || !email.trim()} className={BUTTON_CLASS}>
            {loading ? t('auth.login.sendingCode') : t('auth.login.sendCode')}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground block">
            {t('auth.login.backToHome')}
          </Link>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
